/** One rule row: compact collapsed line (NOT chip · label · summary · expand ·
 *  delete), expanding into the template picker + the tpl's argument editor.
 *  Deprecated / unknown-template / malformed rows render read-only. */
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  Collapse,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMemo } from 'react';

import { RULE_TYPE_EXPR } from '@/api/notificationRulesets';
import type { ContextSchemaSource } from '@/components/TemplateCodeEditor/types';
import { ValueActions } from '@/components/ValueActions/ValueActions';

import { rulesetActions } from '../notificationLinks';
import { ARG_EDITORS, RawArgsView } from './argEditors';
import { ExprRuleEditor } from './ExprRuleEditor';
import {
  ADD_MENU_GROUPS,
  DEPRECATED_TPLS,
  NO_ARGS_TPLS,
  RULE_TEMPLATE_LABELS,
  ruleTemplateLabel,
  TPL,
} from './ruleCatalog';
import { withTemplate } from './ruleSerialize';
import { summarizeRule } from './ruleSummary';
import type { RuleDraft } from './ruleTypes';

export interface RuleRowProps {
  rule: RuleDraft;
  expanded: boolean;
  onToggleExpanded: () => void;
  onChange: (patch: Partial<RuleDraft>) => void;
  /** Full-row replacement (template switch resets args). */
  onReplace: (rule: RuleDraft) => void;
  onRemove: () => void;
  tenantId: number | 'system';
  excludeRulesetId?: number;
  error?: string;
  /** Non-blocking: the stored rule breaks the rule-args schema (amber hint). */
  warning?: string;
  disabled?: boolean;
  contextSchemas?: ContextSchemaSource[];
}

interface TplOption {
  tpl: number;
  label: string;
  group: string;
}

const TPL_OPTIONS: TplOption[] = ADD_MENU_GROUPS.flatMap((g) =>
  g.tpls.map((tpl) => ({ tpl, label: RULE_TEMPLATE_LABELS[tpl] ?? `#${tpl}`, group: g.label })),
);

export function RuleRow({
  rule,
  expanded,
  onToggleExpanded,
  onChange,
  onReplace,
  onRemove,
  tenantId,
  excludeRulesetId,
  error,
  warning,
  disabled,
  contextSchemas,
}: RuleRowProps) {
  const isExpr = rule.type === RULE_TYPE_EXPR;
  const tpl = rule.tpl;
  const isDeprecated = !isExpr && tpl !== null && DEPRECATED_TPLS.has(tpl);
  const isUnknown = !isExpr && tpl !== null && !(tpl in RULE_TEMPLATE_LABELS) && !isDeprecated;
  const readOnlyKind = isDeprecated || isUnknown;

  const title = isExpr ? 'Expression' : ruleTemplateLabel(tpl);
  const summary = useMemo(() => summarizeRule(rule), [rule]);

  // An "Include ruleset" row names exactly one record, so its collapsed line
  // can reach it without expanding — every other summary is a multi-value
  // ellipsized string whose parts are configured in the body below.
  const nested = !isExpr && tpl === TPL.RULESET ? (rule.nested_ruleset ?? null) : null;

  const ArgsEditor = !isExpr && tpl !== null ? ARG_EDITORS[tpl] : undefined;
  const hasBody = isExpr || readOnlyKind || ArgsEditor !== undefined || !NO_ARGS_TPLS.has(tpl ?? -1);
  const selectedOption = TPL_OPTIONS.find((o) => o.tpl === tpl) ?? null;

  return (
    <Box
      sx={{
        border: 1,
        borderColor: error ? 'error.main' : warning ? 'warning.main' : 'divider',
        borderRadius: 1,
        px: 1.5,
        py: 0.75,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ minHeight: 32 }}>
        <Tooltip title={rule.negate ? 'Rule is negated — click to un-negate' : 'Negate this rule'}>
          <span>
            <Chip
              label="NOT"
              size="small"
              color={rule.negate ? 'error' : 'default'}
              variant={rule.negate ? 'filled' : 'outlined'}
              disabled={disabled}
              onClick={() => onChange({ negate: !rule.negate })}
              sx={{
                fontWeight: 700,
                fontSize: 11,
                width: 46,
                ...(rule.negate ? {} : { color: 'text.disabled', borderColor: 'divider' }),
              }}
            />
          </span>
        </Tooltip>
        <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
          {title}
        </Typography>
        {isDeprecated && <Chip label="deprecated" size="small" color="warning" variant="outlined" />}
        {isUnknown && <Chip label="unknown" size="small" color="warning" variant="outlined" />}
        {isExpr && <Chip label="advanced" size="small" variant="outlined" sx={{ color: 'text.secondary' }} />}
        <Typography
          variant="body2"
          color="text.secondary"
          noWrap
          sx={{ flex: 1, minWidth: 0, fontFamily: isExpr ? 'monospace' : undefined, fontSize: isExpr ? 12 : undefined }}
        >
          {nested ? (
            <ValueActions actions={rulesetActions(nested)}>{summary}</ValueActions>
          ) : (
            summary
          )}
        </Typography>
        {hasBody && (
          <IconButton
            size="small"
            aria-label={expanded ? 'Collapse rule' : 'Edit rule'}
            onClick={onToggleExpanded}
          >
            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        )}
        <IconButton size="small" aria-label="Remove rule" onClick={onRemove} disabled={disabled}>
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Stack>

      {error && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
          {error}
        </Typography>
      )}
      {!error && warning && (
        <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
          {warning} This rule is saved unchanged; editing it will require fixing this.
        </Typography>
      )}

      {hasBody && (
        <Collapse in={expanded} unmountOnExit>
          <Box sx={{ pt: 1.5, pb: 0.75 }}>
            {isExpr ? (
              <ExprRuleEditor
                rule={rule}
                onRuleChange={onChange}
                disabled={disabled}
                contextSchemas={contextSchemas}
              />
            ) : readOnlyKind ? (
              <Stack spacing={1}>
                <Alert severity="warning" variant="outlined">
                  {isDeprecated
                    ? 'This rule template is deprecated — it still evaluates, but can no longer be configured. It is preserved as-is on save.'
                    : 'This rule template is not recognized by the console. It is preserved as-is on save.'}
                </Alert>
                <RawArgsView rule={rule} />
              </Stack>
            ) : (
              <Stack spacing={1.5}>
                <Autocomplete<TplOption>
                  size="small"
                  options={TPL_OPTIONS}
                  groupBy={(o) => o.group}
                  getOptionLabel={(o) => o.label}
                  isOptionEqualToValue={(a, b) => a.tpl === b.tpl}
                  value={selectedOption}
                  disabled={disabled}
                  onChange={(_, opt) => {
                    // Ignore clears — a rule always has a condition; the value
                    // prop snaps the input back to the current template.
                    if (opt && opt.tpl !== tpl) onReplace(withTemplate(rule, opt.tpl));
                  }}
                  sx={{ maxWidth: 440 }}
                  renderInput={(params) => <TextField {...params} label="Condition" />}
                />
                {ArgsEditor && (
                  <ArgsEditor
                    rule={rule}
                    onArgsChange={(args) => onChange({ args })}
                    onRuleChange={onChange}
                    tenantId={tenantId}
                    excludeRulesetId={excludeRulesetId}
                    disabled={disabled}
                    contextSchemas={contextSchemas}
                  />
                )}
              </Stack>
            )}
          </Box>
        </Collapse>
      )}
    </Box>
  );
}
